import React, { useMemo } from 'react';
import * as THREE from 'three';
import { RoundedBox } from '@react-three/drei';
import { getSharedStandardMaterial } from '../../../../utils/threeResourcePool';

export type FOUPProps = {
  /** Uniform scale relative to standard ~0.40m cubic carrier envelope. */
  scale?: number;
  /** Optional shell tint override (defaults to premium cleanroom satin white + smoked glass). */
  color?: string;
  opacity?: number;
  position?: [number, number, number];
  rotation?: [number, number, number];
};

/**
 * High-End Futuristic Semiconductor 300mm FOUP (Front Opening Unified Pod).
 *
 * Engineered Semiconductor Wafer Transport Container Features:
 * 1. OUTER HOUSING: Satin white/silver structural exoskeleton with precision bevels and dark graphite frame.
 * 2. SMOKED GLASS PANELS: Premium dark smoked transparent glass with subtle blue-cyan tint and Fresnel reflections.
 * 3. FRONT RECESSED DOOR: Machined dark graphite front door with brushed stainless rotary latch & locking arms.
 * 4. INTERNAL WAFER CHAMBER: Precision cassette rack structure with stacked mirror-polished silicon wafer discs.
 * 5. REINFORCED CORNERS: 4 structural corner columns with machined brackets and chrome hex fasteners.
 * 6. TOP & BOTTOM INTERFACES: SEMI standard low-profile robotic handling flange on top, 3-point kinematic locating coupling on bottom.
 */
export const FOUP: React.FC<FOUPProps> = ({
  scale = 1,
  color,
  opacity = 1,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
}) => {
  const a = opacity < 1 ? opacity : 1;

  const mats = useMemo(() => {
    // Single Unified Dark Blue Metallic FOUP Material Specification
    const shellColor = color && color !== 'white' && color !== '#FFFFFF' && color !== 'black' && !color.includes('#F8') ? color : '#0F2B5C';

    return {
      // 1. Dark Blue Metallic Polymer Outer Housing
      satinHousing: getSharedStandardMaterial({
        color: shellColor,
        metalness: 0.86,
        roughness: 0.18,
        envMapIntensity: 1.4,
        transparent: a < 1,
        opacity: a,
      }),
      // 2. Deep Midnight Navy Metallic Structural Frame & Chassis
      darkGraphite: getSharedStandardMaterial({
        color: '#081730',
        metalness: 0.88,
        roughness: 0.15,
        envMapIntensity: 1.35,
        transparent: a < 1,
        opacity: a,
      }),
      // 3. Machined Dark Metallic Blue Front Door Panel
      frontDoor: getSharedStandardMaterial({
        color: '#0A1C36',
        metalness: 0.84,
        roughness: 0.20,
        envMapIntensity: 1.25,
        transparent: a < 1,
        opacity: a,
      }),
      // 4. Premium Dark Smoked Glass with Subtle Blue/Cyan Tint
      smokedGlass: new THREE.MeshPhysicalMaterial({
        color: '#0F2430',
        metalness: 0.08,
        roughness: 0.08,
        transmission: 0.76,
        thickness: 0.22,
        ior: 1.52,
        transparent: true,
        opacity: Math.min(0.55, a * 0.6),
        envMapIntensity: 1.25,
        specularIntensity: 0.85,
        attenuationColor: new THREE.Color('#0A1E2C'),
        attenuationDistance: 2.2,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
      // 5. Brushed Stainless Steel Rotary Latch Mechanism & Fasteners
      stainlessSteel: getSharedStandardMaterial({
        color: '#CBD5E1',
        metalness: 0.94,
        roughness: 0.16,
        envMapIntensity: 1.35,
        transparent: a < 1,
        opacity: a,
      }),
      // 6. Polished Chrome Center Keyway Handle & Hex Bolts
      polishedChrome: getSharedStandardMaterial({
        color: '#FFFFFF',
        metalness: 0.98,
        roughness: 0.06,
        envMapIntensity: 1.5,
        transparent: a < 1,
        opacity: a,
      }),
      // 7. Dark Precision Gasket & Seal Ring
      sealGasket: getSharedStandardMaterial({
        color: '#090C0F',
        metalness: 0.5,
        roughness: 0.55,
        opacity: 1,
      }),
      // 8. Mirror-Polished Silicon Wafer Discs
      siliconWafer: getSharedStandardMaterial({
        color: '#94A3B8',
        metalness: 0.96,
        roughness: 0.08,
        envMapIntensity: 1.4,
        transparent: a < 1,
        opacity: a,
      }),
      // 9. Subtle Cyan Cleanroom Status LED Indicator
      cyanStatusLed: getSharedStandardMaterial({
        color: '#00E5FF',
        emissive: '#00E5FF',
        emissiveIntensity: 0.95,
        toneMapped: false,
      }),
      // 10. Side Filter / Purge Accent Strip
      purgeFilter: getSharedStandardMaterial({
        color: '#1E293B',
        metalness: 0.6,
        roughness: 0.35,
        opacity: a,
      }),
    };
  }, [a, color]);

  // Standardized 300mm FOUP Dimensions (Proportionate: Width 0.40, Height 0.42, Depth 0.42)
  const w = 0.40;
  const h = 0.42;
  const d = 0.42;
  const bodyY = h * 0.5;

  return (
    <group name="SemiconductorFOUP" position={position} rotation={rotation} scale={scale}>
      {/* ========================================================================= */}
      {/* 1. INTERNAL SILICON WAFER STORAGE CHAMBER & CASSETTE RACKS                */}
      {/* ========================================================================= */}
      <group position={[0, bodyY, 0]} name="InternalWaferChamber">
        {/* Dark Internal Cavity Box */}
        <mesh position={[0, 0, -d * 0.02]}>
          <boxGeometry args={[w * 0.86, h * 0.88, d * 0.86]} />
          <meshStandardMaterial color="#0A0E13" roughness={0.7} />
        </mesh>

        {/* Vertical Cassette Rack Guide Pillars (Left & Right) */}
        {[-w * 0.36, w * 0.36].map((gx, gIdx) => (
          <mesh key={`guide-rack-${gIdx}`} position={[gx, 0, -d * 0.02]} material={mats.darkGraphite}>
            <boxGeometry args={[0.016, h * 0.82, 0.03]} />
          </mesh>
        ))}

        {/* Stack of Mirror-Polished Thin Circular Silicon Wafer Discs (8 Wafers) */}
        {[-0.14, -0.10, -0.06, -0.02, 0.02, 0.06, 0.10, 0.14].map((wy, wIdx) => (
          <mesh
            key={`silicon-wafer-${wIdx}`}
            position={[0, wy, -d * 0.02]}
            rotation={[Math.PI / 2, 0, 0]}
            material={mats.siliconWafer}
            castShadow
          >
            <cylinderGeometry args={[w * 0.36, w * 0.36, 0.0025, 32]} />
          </mesh>
        ))}
      </group>

      {/* ========================================================================= */}
      {/* 2. DARK SMOKED GLASS ENCLOSURE (SIDES & REAR)                             */}
      {/* ========================================================================= */}
      {/* Left Smoked Glass Window (-X) */}
      <mesh position={[-w * 0.501, bodyY, -d * 0.02]} material={mats.smokedGlass} receiveShadow>
        <boxGeometry args={[0.008, h * 0.78, d * 0.76]} />
      </mesh>
      {/* Right Smoked Glass Window (+X) */}
      <mesh position={[w * 0.501, bodyY, -d * 0.02]} material={mats.smokedGlass} receiveShadow>
        <boxGeometry args={[0.008, h * 0.78, d * 0.76]} />
      </mesh>
      {/* Rear Smoked Glass Window (-Z) */}
      <mesh position={[0, bodyY, -d * 0.501]} material={mats.smokedGlass} receiveShadow>
        <boxGeometry args={[w * 0.76, h * 0.78, 0.008]} />
      </mesh>

      {/* ========================================================================= */}
      {/* 3. SATIN WHITE/SILVER STRUCTURAL EXOSKELETON HOUSING                      */}
      {/* ========================================================================= */}
      {/* Main Structural Outer Body Shell */}
      <RoundedBox
        args={[w, h, d]}
        radius={0.026}
        smoothness={4}
        position={[0, bodyY, 0]}
        castShadow
        receiveShadow
      >
        <primitive object={mats.satinHousing} attach="material" />
      </RoundedBox>

      {/* Top & Bottom Horizontal Dark Graphite Structural Trim Bands */}
      {[-h * 0.44, h * 0.44].map((by, bIdx) => (
        <group key={`trim-band-${bIdx}`} position={[0, bodyY + by, 0]}>
          <mesh position={[0, 0, d * 0.504]} material={mats.darkGraphite}>
            <boxGeometry args={[w * 0.94, 0.022, 0.008]} />
          </mesh>
          <mesh position={[0, 0, -d * 0.504]} material={mats.darkGraphite}>
            <boxGeometry args={[w * 0.94, 0.022, 0.008]} />
          </mesh>
          <mesh position={[-w * 0.504, 0, 0]} material={mats.darkGraphite}>
            <boxGeometry args={[0.008, 0.022, d * 0.94]} />
          </mesh>
          <mesh position={[w * 0.504, 0, 0]} material={mats.darkGraphite}>
            <boxGeometry args={[0.008, 0.022, d * 0.94]} />
          </mesh>
        </group>
      ))}

      {/* ========================================================================= */}
      {/* 4. REINFORCED STRUCTURAL CORNER ASSEMBLIES WITH HEX FASTENERS             */}
      {/* ========================================================================= */}
      {[-w * 0.47, w * 0.47].map((cx, xIdx) =>
        [-d * 0.47, d * 0.47].map((cz, zIdx) => (
          <group key={`corner-assembly-${xIdx}-${zIdx}`} position={[cx, bodyY, cz]}>
            {/* Vertical Corner Reinforcing Bracket */}
            <mesh material={mats.darkGraphite} castShadow>
              <boxGeometry args={[0.038, h * 0.96, 0.038]} />
            </mesh>
            {/* Top Corner Beveled Cap */}
            <mesh position={[0, h * 0.46, 0]} material={mats.satinHousing} castShadow>
              <boxGeometry args={[0.046, 0.032, 0.046]} />
            </mesh>
            {/* Bottom Corner Beveled Cap */}
            <mesh position={[0, -h * 0.46, 0]} material={mats.satinHousing} castShadow>
              <boxGeometry args={[0.046, 0.032, 0.046]} />
            </mesh>
            {/* Precision Chrome Hex Fasteners */}
            <mesh position={[0, h * 0.38, zIdx === 1 ? 0.022 : -0.022]} material={mats.polishedChrome}>
              <cylinderGeometry args={[0.006, 0.006, 0.004, 6]} />
            </mesh>
            <mesh position={[0, -h * 0.38, zIdx === 1 ? 0.022 : -0.022]} material={mats.polishedChrome}>
              <cylinderGeometry args={[0.006, 0.006, 0.004, 6]} />
            </mesh>
          </group>
        ))
      )}

      {/* ========================================================================= */}
      {/* 5. RECESSED FRONT DOOR & PRECISION MECHANICAL ROTARY LATCH                */}
      {/* ========================================================================= */}
      <group position={[0, bodyY, d * 0.505]} name="FOUPFrontDoor">
        {/* Perimeter Seal Gasket */}
        <mesh position={[0, 0, -0.002]} material={mats.sealGasket}>
          <boxGeometry args={[w * 0.82, h * 0.84, 0.008]} />
        </mesh>

        {/* Large Recessed Dark Graphite Front Door Panel */}
        <RoundedBox
          args={[w * 0.80, h * 0.82, 0.014]}
          radius={0.018}
          smoothness={3}
          position={[0, 0, 0.006]}
          castShadow
        >
          <primitive object={mats.frontDoor} attach="material" />
        </RoundedBox>

        {/* Front Door Top Status LED Indicator (Subtle Cyan Glow) */}
        <mesh position={[0, h * 0.34, 0.015]} material={mats.cyanStatusLed}>
          <boxGeometry args={[0.04, 0.006, 0.004]} />
        </mesh>

        {/* Central Rotary Latch Locking Mechanism */}
        <group position={[0, 0, 0.015]}>
          {/* Outer Brushed Stainless Steel Dial Ring */}
          <mesh rotation={[Math.PI / 2, 0, 0]} material={mats.stainlessSteel} castShadow>
            <cylinderGeometry args={[0.054, 0.054, 0.010, 32]} />
          </mesh>
          {/* Inner Dark Precision Ring */}
          <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.003]} material={mats.sealGasket}>
            <cylinderGeometry args={[0.042, 0.042, 0.011, 24]} />
          </mesh>
          {/* Center Stainless Steel Faceplate */}
          <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.006]} material={mats.stainlessSteel}>
            <cylinderGeometry args={[0.036, 0.036, 0.010, 24]} />
          </mesh>
          {/* Rectangular Keyway / Robot Engagement Handle */}
          <mesh position={[0, 0, 0.013]} material={mats.polishedChrome} castShadow>
            <boxGeometry args={[0.022, 0.032, 0.012]} />
          </mesh>
          {/* Keyway Center Slot */}
          <mesh position={[0, 0, 0.020]} material={mats.sealGasket}>
            <boxGeometry args={[0.008, 0.020, 0.004]} />
          </mesh>

          {/* Dual Stainless Steel Locking Latch Arms (Left & Right) */}
          {[-0.065, 0.065].map((lx, lIdx) => (
            <group key={`latch-arm-${lIdx}`} position={[lx, 0, 0.005]}>
              <mesh material={mats.stainlessSteel} castShadow>
                <boxGeometry args={[0.030, 0.020, 0.008]} />
              </mesh>
              {/* Latch Hex Pivot Pin */}
              <mesh position={[0, 0, 0.005]} material={mats.polishedChrome}>
                <cylinderGeometry args={[0.004, 0.004, 0.003, 6]} />
              </mesh>
            </group>
          ))}
        </group>

        {/* Lower Alignment Indicator Bar */}
        <mesh position={[0, -h * 0.32, 0.014]} material={mats.darkGraphite}>
          <boxGeometry args={[w * 0.42, 0.012, 0.004]} />
        </mesh>
      </group>

      {/* ========================================================================= */}
      {/* 6. SIDE FILTER & PURGE PORT PANELS                                        */}
      {/* ========================================================================= */}
      {[-w * 0.504, w * 0.504].map((sx, sIdx) => (
        <group key={`purge-panel-${sIdx}`} position={[sx, bodyY, 0]}>
          {/* Recessed Purge Valve Block */}
          <mesh position={[0, -h * 0.28, 0]} material={mats.purgeFilter}>
            <boxGeometry args={[0.008, 0.05, 0.12]} />
          </mesh>
          {/* Dual Precision Purge Valves */}
          {[-0.035, 0.035].map((pz, pIdx) => (
            <mesh key={`valve-${pIdx}`} position={[0, -h * 0.28, pz]} rotation={[0, 0, Math.PI / 2]} material={mats.stainlessSteel}>
              <cylinderGeometry args={[0.008, 0.008, 0.012, 16]} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ========================================================================= */}
      {/* 7. TOP ROBOTIC AUTOMATION HANDLING FLANGE (LOW PROFILE SEMI T-BAR)        */}
      {/* ========================================================================= */}
      <group position={[0, h + 0.008, 0]} name="TopRoboticFlange">
        {/* Recessed Top Platform Plate */}
        <mesh position={[0, 0, 0]} material={mats.darkGraphite} castShadow>
          <boxGeometry args={[w * 0.46, 0.014, d * 0.46]} />
        </mesh>
        {/* SEMI Standard Robotic Gripper Flange (Low-Profile Chamfered Flange) */}
        <mesh position={[0, 0.010, 0]} material={mats.satinHousing} castShadow>
          <boxGeometry args={[w * 0.38, 0.008, d * 0.38]} />
        </mesh>
        {/* Center Precision Stainless Alignment Cup */}
        <mesh position={[0, 0.015, 0]} rotation={[Math.PI / 2, 0, 0]} material={mats.stainlessSteel}>
          <cylinderGeometry args={[0.036, 0.036, 0.006, 24]} />
        </mesh>
      </group>

      {/* ========================================================================= */}
      {/* 8. BOTTOM KINEMATIC COUPLING INTERFACE (OHT & LIFTER NESTING PADS)        */}
      {/* ========================================================================= */}
      <group position={[0, -0.005, 0]} name="BottomKinematicInterface">
        {/* Bottom Precision Base Plate */}
        <mesh position={[0, 0.004, 0]} material={mats.darkGraphite} receiveShadow>
          <boxGeometry args={[w * 0.94, 0.008, d * 0.94]} />
        </mesh>

        {/* 4 Corner Kinematic Nesting Contact Pads */}
        {[-w * 0.38, w * 0.38].map((px, xIdx) =>
          [-d * 0.38, d * 0.38].map((pz, zIdx) => (
            <mesh key={`pad-${xIdx}-${zIdx}`} position={[px, -0.004, pz]} material={mats.stainlessSteel}>
              <boxGeometry args={[0.042, 0.008, 0.042]} />
            </mesh>
          ))
        )}

        {/* 3 SEMI Standard Triangular Kinematic Locating Pin Sockets */}
        {[
          [0, -0.004, -d * 0.28],
          [-w * 0.24, -0.004, d * 0.20],
          [w * 0.24, -0.004, d * 0.20],
        ].map(([kx, ky, kz], kIdx) => (
          <mesh key={`socket-${kIdx}`} position={[kx, ky, kz]} rotation={[Math.PI / 2, 0, 0]} material={mats.sealGasket}>
            <cylinderGeometry args={[0.016, 0.016, 0.010, 16]} />
          </mesh>
        ))}
      </group>
    </group>
  );
};

export default React.memo(FOUP);
